"use client";

import * as React from "react";
import type { Key } from "react-aria-components";
import { ComboBoxStateContext } from "react-aria-components/ComboBox";
import { File as FileGlyph, Folder } from "@gravity-ui/icons";
import { ComboBox, Input, Label, ListBox, Spinner } from "@heroui/react";

/**
 * 树形选择（HeroUI `ComboBox` + 按深度缩进的扁平列表）
 *
 * HeroUI Pro `FileTree` 内部使用 RAC `Collection`，与 `ComboBox`/`Select` 根上的 `CollectionBuilder`
 * 不能放在同一 React 子树里，否则会触发集合长度错误。此处用文件夹/文件图标 + 按深度缩进，
 * 在 `ListBox` 中呈现先序展开后的树，外观与 HeroUI 默认 ComboBox 一致。
 */
export interface TreeSelectNode {
  id: string;
  label: string;
  children?: TreeSelectNode[];
}

type TreeSelectFlatRow = {
  id: string;
  label: string;
  /** 0 为根下第一层 */
  level: number;
  hasChildren: boolean;
};

export interface TreeSelectProps {
  items: TreeSelectNode[];
  "aria-label": string;
  selectionMode: "single" | "multiple";
  value: string[] | string | null | undefined;
  onChange: (ids: string[] | string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  /** 无节点时的提示文案 */
  emptyMessage?: string;
  /** 多选时输入框摘要：`count` 为已选数量（≥2） */
  multiSelectionSummary?: (count: number) => string;
  /** 下拉触发按钮的无障碍说明 */
  listTriggerAriaLabel?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

function mergeRefs<T>(...refs: Array<React.Ref<T> | null | undefined>) {
  return (value: T | null) => {
    for (const r of refs) {
      if (r == null) continue;
      if (typeof r === "function") (r as (instance: T | null) => void)(value);
      else (r as React.MutableRefObject<T | null>).current = value;
    }
  };
}

function flattenTreePreorder(nodes: TreeSelectNode[], level = 0, out: TreeSelectFlatRow[] = []) {
  for (const n of nodes) {
    const hasChildren = !!(n.children && n.children.length > 0);
    out.push({ id: n.id, label: n.label, level, hasChildren });
    if (n.children?.length) flattenTreePreorder(n.children, level + 1, out);
  }
  return out;
}

function findLabelInTree(nodes: TreeSelectNode[], id: string): string | undefined {
  for (const n of nodes) {
    if (n.id === id) return n.label;
    if (n.children?.length) {
      const hit = findLabelInTree(n.children, id);
      if (hit) return hit;
    }
  }
  return undefined;
}

function formatInputSummary(
  items: TreeSelectNode[],
  value: TreeSelectProps["value"],
  mode: "single" | "multiple",
  multiSummary: (count: number) => string
) {
  if (mode === "single") {
    const id = typeof value === "string" ? value : null;
    if (!id) return "";
    return findLabelInTree(items, id) ?? id;
  }
  const arr = Array.isArray(value) ? value : [];
  if (arr.length === 0) return "";
  if (arr.length === 1) return findLabelInTree(items, arr[0]!) ?? arr[0]!;
  return multiSummary(arr.length);
}

function RowGlyph({ hasChildren }: { hasChildren: boolean }) {
  return hasChildren ? <Folder className="text-muted shrink-0" /> : <FileGlyph className="text-muted shrink-0" />;
}

function TreeComboInputGroup({
  children,
  disabled,
}: {
  children: React.ReactNode;
  disabled?: boolean;
}) {
  const state = React.useContext(ComboBoxStateContext);
  return (
    <ComboBox.InputGroup
      onPointerDown={(e) => {
        if (disabled || e.button !== 0) return;
        const t = e.target as HTMLElement;
        if (t.closest('[data-slot="combo-box-trigger"]')) return;
        if (!state?.isOpen) state?.open();
      }}
    >
      {children}
    </ComboBox.InputGroup>
  );
}

export const TreeSelect = React.forwardRef<HTMLElement, TreeSelectProps>(function TreeSelect(
  {
    items,
    "aria-label": ariaLabel,
    selectionMode,
    value,
    onChange,
    disabled,
    placeholder = "请选择",
    isLoading,
    emptyMessage = "暂无数据",
    multiSelectionSummary = (count) => `已选 ${count} 项`,
    listTriggerAriaLabel = "展开或折叠列表",
    isInvalid,
    isRequired,
    onBlur,
  },
  ref
) {
  const flatRows = React.useMemo(() => flattenTreePreorder(items), [items]);
  const summary = React.useMemo(
    () => formatInputSummary(items, value, selectionMode, multiSelectionSummary),
    [items, value, selectionMode, multiSelectionSummary]
  );

  const inputValue = summary || "";

  const handleChange = React.useCallback(
    (arg: Key | Key[] | null) => {
      if (selectionMode === "single") {
        const key = Array.isArray(arg) ? (arg[0] ?? null) : arg;
        onChange(key != null ? String(key) : null);
        return;
      }
      if (arg == null) {
        onChange([]);
        return;
      }
      if (Array.isArray(arg)) {
        onChange(arg.map((k) => String(k)));
        return;
      }
      const id = String(arg);
      const arr = Array.isArray(value) ? [...value] : [];
      const i = arr.indexOf(id);
      if (i >= 0) arr.splice(i, 1);
      else arr.push(id);
      onChange(arr);
    },
    [onChange, selectionMode, value]
  );

  if (isLoading) {
    return (
      <div
        ref={mergeRefs(ref)}
        className="flex items-center justify-center py-6 outline-none"
        tabIndex={-1}
      >
        <Spinner className="size-6" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        ref={mergeRefs(ref)}
        className="text-muted-foreground py-4 text-center text-sm outline-none"
        tabIndex={-1}
      >
        {emptyMessage}
      </div>
    );
  }

  if (selectionMode === "single") {
    const selectedKey = typeof value === "string" && value ? value : null;
    return (
      <ComboBox<TreeSelectFlatRow>
        allowsEmptyCollection
        aria-label={ariaLabel}
        defaultFilter={() => true}
        fullWidth
        inputValue={inputValue}
        isDisabled={disabled}
        isInvalid={isInvalid}
        isReadOnly
        isRequired={isRequired}
        items={flatRows}
        menuTrigger="focus"
        selectedKey={selectedKey ?? undefined}
        selectionMode="single"
        onChange={handleChange}
        onInputChange={() => {}}
      >
        <Label className="sr-only">{ariaLabel}</Label>
        <TreeComboInputGroup disabled={disabled}>
          <Input ref={mergeRefs(ref)} placeholder={placeholder} onBlur={onBlur} />
          <ComboBox.Trigger aria-label={listTriggerAriaLabel} />
        </TreeComboInputGroup>
        <ComboBox.Popover placement="bottom start">
          <ListBox aria-label={ariaLabel}>
            {(item: TreeSelectFlatRow) => (
              <ListBox.Item id={item.id} textValue={item.label}>
                <div
                  className="flex min-w-0 items-center gap-2 py-0.5"
                  style={{ paddingLeft: `${item.level * 14}px` }}
                >
                  <RowGlyph hasChildren={item.hasChildren} />
                  <Label className="truncate text-sm">{item.label}</Label>
                </div>
                <ListBox.ItemIndicator />
              </ListBox.Item>
            )}
          </ListBox>
        </ComboBox.Popover>
      </ComboBox>
    );
  }

  const selectedKeys = React.useMemo(() => new Set(Array.isArray(value) ? value : []), [value]);
  const multiComboProps = {
    allowsEmptyCollection: true,
    'aria-label': ariaLabel,
    defaultFilter: () => true,
    fullWidth: true,
    inputValue,
    isDisabled: disabled,
    isInvalid,
    isReadOnly: true,
    isRequired,
    items: flatRows,
    menuTrigger: "focus" as const,
    selectedKeys,
    selectionMode: "multiple" as const,
    onChange: handleChange,
    onInputChange: () => {},
  } as unknown as React.ComponentProps<typeof ComboBox<TreeSelectFlatRow>>;
  return (
    <ComboBox<TreeSelectFlatRow> {...multiComboProps}>
      <Label className="sr-only">{ariaLabel}</Label>
      <TreeComboInputGroup disabled={disabled}>
        <Input ref={mergeRefs(ref)} placeholder={placeholder} onBlur={onBlur} />
        <ComboBox.Trigger aria-label={listTriggerAriaLabel} />
      </TreeComboInputGroup>
      <ComboBox.Popover placement="bottom start">
        <ListBox aria-label={ariaLabel}>
          {(item: TreeSelectFlatRow) => (
            <ListBox.Item id={item.id} textValue={item.label}>
              <div
                className="flex min-w-0 items-center gap-2 py-0.5"
                style={{ paddingLeft: `${item.level * 14}px` }}
              >
                <RowGlyph hasChildren={item.hasChildren} />
                <Label className="truncate text-sm">{item.label}</Label>
              </div>
              <ListBox.ItemIndicator />
            </ListBox.Item>
          )}
        </ListBox>
      </ComboBox.Popover>
    </ComboBox>
  );
});

TreeSelect.displayName = "TreeSelect";
